import { Controller } from "@hotwired/stimulus"
import JSZip from "jszip"

// Client-side pre-check + drag-and-drop for the export.zip upload. Reveals the
// filebox (name + size) and runs three fast, in-browser checks — archive
// readable, Apple Health export detected, substantial dataset — before the
// upload is allowed. Large archives (>5 MB, i.e. real exports) skip the
// in-browser read and defer to server-side validation. This is a fast fail
// only; the worker re-validates everything server-side.
export default class extends Controller {
  static targets = ["input", "error", "filebox", "filename", "filesize", "drop", "v1", "v2", "v3", "v4", "submit"]
  static values = { messages: Object, minAge: { type: Number, default: 18 } }

  connect() {
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener("change", () => this.onChange())
    }
  }

  onChange() {
    const file = this.inputTarget.files[0]
    if (file) this.handleFile(file)
  }

  // ---- Drag & drop ----------------------------------------------------------
  dragover(event) {
    event.preventDefault()
    if (this.hasDropTarget) this.dropTarget.classList.add("hot")
  }

  dragleave(event) {
    event.preventDefault()
    if (this.hasDropTarget) this.dropTarget.classList.remove("hot")
  }

  drop(event) {
    event.preventDefault()
    if (this.hasDropTarget) this.dropTarget.classList.remove("hot")
    const files = event.dataTransfer && event.dataTransfer.files
    if (files && files.length && this.hasInputTarget) {
      // Populate the real input so Uppy's own change handler picks it up too.
      this.inputTarget.files = files
      this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  // ---- Validation -----------------------------------------------------------
  async handleFile(file) {
    // Guard against overlapping runs (rapid re-selection / drag-drop re-dispatch):
    // only the latest invocation may paint check lines, so a slower earlier file
    // can't overwrite a newer selection's result — e.g. a stale "Age verified".
    const run = (this._run = (this._run || 0) + 1)
    this.revealFilebox(file)
    this.hideSubmit()
    this.clearError()
    this.setLine(this.v1Target, null, this.m("v1_init"))
    this.setLine(this.v2Target, null, this.m("v2_init"))
    this.setLine(this.v3Target, null, this.m("v3_init"))
    this.setLine(this.v4Target, null, this.m("v4_init"))

    if (!/\.zip$/i.test(file.name) && (file.type || "").indexOf("zip") < 0) {
      this.setLine(this.v1Target, false, this.m("not_zip"))
      return this.fail(this.m("not_zip_msg"))
    }

    let zip
    try {
      zip = await JSZip.loadAsync(file)
    } catch (err) {
      if (run !== this._run) return
      if (file.size > 5 * 1024 * 1024) {
        // Real exports are large; JSZip can't hold them in memory. Defer to
        // the server, which re-validates everything. Age was never established
        // in-browser, so v4 uses the neutral deferred line (not a green ✓).
        this.setLine(this.v1Target, true, this.m("large_ok_1"))
        this.setLine(this.v2Target, true, this.m("large_ok_2"))
        this.setLine(this.v3Target, true, this.m("large_ok_3"))
        this.setInfo(this.v4Target, this.m("age_server"))
        return this.pass()
      }
      this.setLine(this.v1Target, false, this.m("cant_read"))
      return this.fail(this.m("cant_read_msg"))
    }
    if (run !== this._run) return
    this.setLine(this.v1Target, true, this.m("readable"))

    const key = Object.keys(zip.files).find((k) => k.toLowerCase().endsWith("export.xml"))
    if (!key) {
      this.setLine(this.v2Target, false, this.m("no_export"))
      return this.fail(this.m("no_export_msg"))
    }
    this.setLine(this.v2Target, true, this.m("detected"))

    const entry = zip.files[key]
    const uncompressed = (entry && entry._data && entry._data.uncompressedSize) || 0
    try {
      if (uncompressed > 200000) {
        this.setLine(this.v3Target, true, this.m("substantial"))
        // XML not read in-browser at this size; age is confirmed server-side.
        this.setInfo(this.v4Target, this.m("age_server"))
      } else {
        const xml = await entry.async("string")
        if (run !== this._run) return
        const hasRoot = xml.indexOf("<HealthData") > -1
        const recordCount = (xml.match(/<Record/g) || []).length
        if (!hasRoot) {
          this.setLine(this.v3Target, false, this.m("unexpected"))
          return this.fail(this.m("unexpected_msg"))
        }
        if (recordCount < 20) {
          this.setLine(this.v3Target, false, this.m("too_little"))
          return this.fail(this.m("too_little_msg"))
        }
        this.setLine(this.v3Target, true, this.m("records").replace("{n}", recordCount.toLocaleString()))
        this.checkAge(xml)
      }
      this.pass()
    } catch (err) {
      if (run !== this._run) return
      this.setLine(this.v3Target, true, this.m("present"))
      this.setInfo(this.v4Target, this.m("age_server"))
      this.pass()
    }
  }

  // ---- Age eligibility (hint-only) ------------------------------------------
  // Mirrors the server's 18+ check on the small-file path: read the <Me> DOB and
  // <ExportDate> from the XML we already have, age the DOB against
  // min(ExportDate, today), and pass when >= minAge. This never blocks — when
  // 18+ can't be established (underage, DOB/ExportDate missing or unparseable)
  // the line defers to the server with neutral, non-coaching copy and the flow
  // still reaches pass(). The raw DOB/age are never stored or shown (only the
  // outcome), matching the server's non-persistence contract.
  //
  // Accepted R9 tradeoff: showing an affirmative "verified" state in readable
  // JS is a mild coaching oracle (an editor of export.xml gets pass/fail
  // feedback). We accept it because the server re-derives age authoritatively
  // and underage still routes to reject/review there; the client is advisory
  // only. Real exports are >5 MB and never reach this affirmative path (they
  // defer to the server above), so the oracle only exists for crafted small
  // files — which the server catches regardless.
  checkAge(xml) {
    const dob = this.parseDate((xml.match(/HKCharacteristicTypeIdentifierDateOfBirth="([^"]+)"/) || [])[1])
    const exportDate = this.parseDate((xml.match(/<ExportDate[^>]*\bvalue="([^"]+)"/) || [])[1])
    const today = this.utcToday()
    // Reference date = min(ExportDate, today); a future-dated export ages
    // against today so a minor can't inflate past the gate. No ExportDate means
    // age can't be established → defer, never a pass.
    const ref = exportDate ? (exportDate < today ? exportDate : today) : null

    if (dob && ref && this.ageInYears(dob, ref) >= this.minAgeValue) {
      this.setLine(this.v4Target, true, this.m("age_verified"))
    } else {
      this.setInfo(this.v4Target, this.m("age_server"))
    }
  }

  // Lenient parse of a leading YYYY-MM-DD into a UTC-midnight Date, else null.
  parseDate(str) {
    const m = String(str || "").match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!m) return null
    const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]))
    return isNaN(d.getTime()) ? null : d
  }

  utcToday() {
    const n = new Date()
    return new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate()))
  }

  ageInYears(dob, ref) {
    let years = ref.getUTCFullYear() - dob.getUTCFullYear()
    const mo = ref.getUTCMonth() - dob.getUTCMonth()
    if (mo < 0 || (mo === 0 && ref.getUTCDate() < dob.getUTCDate())) years -= 1
    return years
  }

  // ---- Helpers --------------------------------------------------------------
  m(key) {
    return (this.hasMessagesValue && this.messagesValue[key]) || ""
  }

  setLine(el, ok, text) {
    if (!el) return
    el.className = ok === null ? "" : ok ? "pass" : "fail"
    el.textContent = (ok === null ? "" : ok ? "✓ " : "✕ ") + text
  }

  // Neutral, non-blocking line: no pass/fail styling, distinct from the "…"
  // init state via a "•" marker. Used for age deferred-to-server outcomes so
  // the check is visibly resolved without ever disabling Submit.
  setInfo(el, text) {
    if (!el) return
    el.className = ""
    el.textContent = "• " + text
  }

  revealFilebox(file) {
    if (this.hasFilenameTarget) this.filenameTarget.textContent = file.name
    if (this.hasFilesizeTarget) this.filesizeTarget.textContent = this.humanSize(file.size)
    if (this.hasFileboxTarget) this.fileboxTarget.style.display = "block"
  }

  humanSize(bytes) {
    if (bytes < 1048576) return `${Math.max(1, Math.round(bytes / 1024))} KB`
    return `${(bytes / 1048576).toFixed(1)} MB`
  }

  pass() {
    this.clearError()
    if (this.hasSubmitTarget) this.submitTarget.classList.remove("hidden")
  }

  hideSubmit() {
    if (this.hasSubmitTarget) this.submitTarget.classList.add("hidden")
  }

  fail(message) {
    this.hideSubmit()
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message || ""
    this.errorTarget.hidden = false
  }

  clearError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = true
  }
}
